import {Button, Text} from '@/components-v2'
import {DragDropProvider} from '@dnd-kit/react'
import {Skeleton} from '@mui/material'
import {useRef, useState} from 'react'
import './CategoriesSection.scss'
import {CategoryItem} from './CategoryItem'

// Closest-target collisions can remain active outside the list, so a pointer
// drop must still be physically inside the sibling list and the scroll area.
const isPointerInsideList = (pointer, target, scrollArea) => {
  const list = target.element?.closest('[data-catalog-list]')
  if (!list || !pointer) return true

  const listBounds = list.getBoundingClientRect()
  const isInsideList =
    pointer.x >= listBounds.left &&
    pointer.x <= listBounds.right &&
    pointer.y >= listBounds.top &&
    pointer.y <= listBounds.bottom
  if (!isInsideList) return false

  const scrollBounds = scrollArea?.getBoundingClientRect()
  return !scrollBounds || (pointer.y >= scrollBounds.top && pointer.y <= scrollBounds.bottom)
}

export const CategoriesSection = ({
  categories,
  isLoading,
  isEditMode,
  disabled,
  onEnterEditMode,
  moveItem,
  getOriginalPosition,
  directlyMoved,
  onInputStateChange,
  onToggleSubcategoryVisibility,
  onDraggingChange,
}) => {
  const [expanded, setExpanded] = useState({})
  const [destination, setDestination] = useState(null)
  const [activeDrag, setActiveDrag] = useState(null)
  const listRef = useRef(null)

  const getDestination = event => {
    const {source, target} = event.operation
    if (!source || !target || source.id === target.id) return null
    if (source.data.parentId !== target.data.parentId) return null

    const parentId = source.data.parentId
    const siblings =
      parentId === null ? categories : (categories.find(category => category.id === parentId)?.subCategories ?? [])
    const from = siblings.findIndex(item => item.id === source.data.itemId)
    const to = siblings.findIndex(item => item.id === target.data.itemId)
    if (from < 0 || to < 0) return null

    if (!isPointerInsideList(event.operation.position.current, target, listRef.current)) return null

    return {
      id: target.data.itemId,
      parentId,
      position: to + 1,
      edge: from < to ? 'after' : 'before',
    }
  }

  const updateDestination = event => setDestination(getDestination(event))

  const handleDragStart = event => {
    const source = event.operation.source
    if (!source) return
    const {itemId, parentId} = source.data
    setActiveDrag({itemId, parentId, wasExpanded: Boolean(expanded[itemId])})
    if (parentId === null) setExpanded(previous => ({...previous, [itemId]: false}))
    onDraggingChange(true)
  }

  const handleDragEnd = event => {
    const drop = event.canceled ? null : getDestination(event)
    if (activeDrag && drop) moveItem(activeDrag.itemId, drop.position, activeDrag.parentId)
    if (activeDrag?.parentId === null && !drop) {
      setExpanded(previous => ({...previous, [activeDrag.itemId]: activeDrag.wasExpanded}))
    }
    setActiveDrag(null)
    setDestination(null)
    onDraggingChange(false)
  }

  return (
    <section className="flex flex-col gap-6 rounded-lg bg-[var(--color-background-layer-01)] p-6">
      <div className="flex items-center justify-between gap-4">
        <Text headingLevel={5}>Categories</Text>
        <Button
          type="button"
          text="Edit Order"
          iconLeading="list_alt"
          variant="secondary"
          size="small"
          disabled={isLoading || disabled || isEditMode || categories.length === 0}
          onClick={onEnterEditMode}
        />
      </div>
      {isLoading ? (
        <div className="flex flex-col gap-6">
          {Array.from({length: 6}, (_, i) => (
            <Skeleton key={i} variant="rectangular" className="rounded-lg" width="100%" height={56} />
          ))}
        </div>
      ) : (
        <DragDropProvider
          onDragStart={handleDragStart}
          onDragMove={updateDestination}
          onDragOver={updateDestination}
          onDragEnd={handleDragEnd}
        >
          <div ref={listRef} data-catalog-list className="flex max-h-[500px] flex-col gap-6 overflow-y-auto p-1">
            {categories.map((category, index) => (
              <CategoryItem
                key={category.id}
                category={category}
                index={index}
                totalCategories={categories.length}
                expanded={Boolean(expanded[category.id])}
                onToggleExpanded={() => {
                  if (!activeDrag && !disabled) {
                    setExpanded(previous => ({...previous, [category.id]: !previous[category.id]}))
                  }
                }}
                onToggleSubcategoryVisibility={onToggleSubcategoryVisibility}
                isEditMode={isEditMode}
                disabled={disabled}
                moveItem={moveItem}
                getOriginalPosition={getOriginalPosition}
                directlyMoved={directlyMoved}
                onInputStateChange={onInputStateChange}
                destination={destination}
              />
            ))}
            {categories.length === 0 && <p>No categories available.</p>}
          </div>
        </DragDropProvider>
      )}
    </section>
  )
}
